/**
 * Unified Navigation Utility
 *
 * Handles all navigation patterns in the app:
 * - In-page tab switching
 * - Cross-page navigation (with HashRouter)
 * - External links (new tab)
 *
 * The app uses HashRouter, so all internal URLs must use /#/ prefix.
 */

/**
 * Get the base path for hash router
 * In development: '' (empty, uses current origin)
 * In production: '' (empty, uses current origin)
 */
const getBasePath = (): string => {
  return window.location.origin;
};

/**
 * Build a hash router URL
 * @param path - The path (e.g., '/system-settings')
 * @param params - Optional query parameters
 * @returns Full URL with hash (e.g., 'http://localhost:3000/#/system-settings?tab=output-schemas')
 */
export const buildHashUrl = (path: string, params?: Record<string, string>): string => {
  const base = getBasePath();
  let url = `${base}/#${path}`;

  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    url += `?${searchParams.toString()}`;
  }

  return url;
};

/**
 * Navigate to a path within the app (same tab)
 * Use this with react-router's navigate function
 * @param navigate - The navigate function from useNavigate()
 * @param path - The path to navigate to
 * @param params - Optional query parameters
 */
export const navigateTo = (
  navigate: (path: string) => void,
  path: string,
  params?: Record<string, string>
): void => {
  let fullPath = path;
  if (params && Object.keys(params).length > 0) {
    const searchParams = new URLSearchParams(params);
    fullPath += `?${searchParams.toString()}`;
  }
  navigate(fullPath);
};

/**
 * Open a path in a new tab
 * @param path - The path (e.g., '/system-settings')
 * @param params - Optional query parameters
 */
export const openInNewTab = (path: string, params?: Record<string, string>): void => {
  const url = buildHashUrl(path, params);
  window.open(url, '_blank');
};

/**
 * Navigation targets for agent config page
 */
export type AgentConfigTab =
  | 'basic'
  | 'hooks'
  | 'memory'
  | 'knowledge'
  | 'astrology'
  | 'emotion'
  | 'classifier'
  | 'tools'
  | 'prompts'
  | 'advanced'
  | 'versions';

/**
 * Navigation targets for system settings page
 */
export type SystemSettingsTab =
  | 'mem0'
  | 'mcp-servers'
  | 'tool-enhancement';

/**
 * Navigation targets for business templates page
 */
export type BusinessTemplatesTab =
  | 'prompt-templates'
  | 'output-schemas';

/**
 * Create navigation handlers for agent config page
 * @param setActiveTab - Tab setter function
 * @returns Object with navigation methods
 */
export const createAgentConfigNavigation = (
  setActiveTab: (tab: AgentConfigTab) => void
) => ({
  /** Navigate to a tab within the agent config page */
  toTab: (tab: AgentConfigTab) => setActiveTab(tab),

  /** Navigate to prompts tab */
  toPrompts: () => setActiveTab('prompts'),

  /** Navigate to memory tab */
  toMemory: () => setActiveTab('memory'),

  /** Navigate to classifier tab */
  toClassifier: () => setActiveTab('classifier'),

  /** Navigate to tools tab */
  toTools: () => setActiveTab('tools'),

  /** Navigate to hooks tab */
  toHooks: () => setActiveTab('hooks'),

  /** Open system settings in new tab */
  toSystemSettings: (tab?: SystemSettingsTab) => {
    openInNewTab('/system-settings', tab ? { tab } : undefined);
  },

  /** Open business templates in new tab */
  toBusinessTemplates: (tab?: BusinessTemplatesTab) => {
    openInNewTab('/business-templates', tab ? { tab } : undefined);
  },

  /** Open output schemas management in new tab */
  toOutputSchemas: () => {
    openInNewTab('/business-templates', { tab: 'output-schemas' });
  },
});

/**
 * Navigation context type for passing to child components
 */
export interface AgentConfigNavigationContext {
  toTab: (tab: AgentConfigTab) => void;
  toPrompts: () => void;
  toMemory: () => void;
  toClassifier: () => void;
  toTools: () => void;
  toHooks: () => void;
  toSystemSettings: (tab?: SystemSettingsTab) => void;
  toBusinessTemplates: (tab?: BusinessTemplatesTab) => void;
  toOutputSchemas: () => void;
}
